import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteMedicalRecord, fetchMedicalRecordDetail } from '../services/profileService';
import { MedicalRecordDetail } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import './MedicalRecordDetailPage.css';

const formatDateTime = (value?: string | null) => {
  if (!value) return '未知';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const metadataLabelMap: Record<string, string> = {
  herb_suggestions: '药材建议',
  patient_info: '患者背景',
  assistant_message: 'AI问诊摘要',
  care_plan_markdown: '医嘱记录',
  symptoms_summary: '症状总结',
  diagnosis_summary: '辨证总结',
  follow_up_questions: '追问列表',
  ready_for_prescription: '处方状态',
  next_stage: '下一阶段',
};

const SUPPLEMENT_RESERVED_KEYS = ['herb_suggestions', 'patient_info', 'assistant_message', 'care_plan_markdown'];

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getMetadataLabel = (key: string) => metadataLabelMap[key] ?? toTitleCase(key);

const renderMarkdownContent = (content: string, size: 'normal' | 'small' = 'normal'): React.ReactElement => (
  <div className={`detail-markdown${size === 'small' ? ' small' : ''}`}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
      {content}
    </ReactMarkdown>
  </div>
);

const renderMetadataValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) {
    return <span className="meta-value">无</span>;
  }

  if (Array.isArray(value)) {
    const hasObjectItems = value.some((item) => item && typeof item === 'object');
    if (hasObjectItems) {
      return (
        <ul className="detail-nested-list">
          {value.map((item, index) => (
            <li key={index}>
              {typeof item === 'object' && item !== null ? (
                <dl>
                  {Object.entries(item).map(([childKey, childValue]) => (
                    <div key={childKey}>
                      <dt>{childKey}</dt>
                      <dd>{renderMetadataValue(childValue)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <span>{renderMetadataValue(item)}</span>
              )}
            </li>
          ))}
        </ul>
      );
    }

    return <span>{value.map((item) => String(item)).join('、')}</span>;
  }

  if (typeof value === 'object') {
    return (
      <dl className="detail-nested-dl">
        {Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) => (
          <div key={childKey}>
            <dt>{childKey}</dt>
            <dd>{renderMetadataValue(childValue)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (typeof value === 'boolean') {
    return <span>{value ? '是' : '否'}</span>;
  }

  return <span>{String(value)}</span>;
};

const renderSpecialMetadata = (key: string, value: unknown): React.ReactNode => {
  if (key === 'assistant_message' && typeof value === 'string' && value.trim().length > 0) {
    return renderMarkdownContent(value, 'small');
  }

  if (key === 'care_plan_markdown' && typeof value === 'string' && value.trim().length > 0) {
    return renderMarkdownContent(value);
  }

  if (key === 'herb_suggestions' && Array.isArray(value)) {
    return (
      <div className="detail-herb-suggestions">
        {value.map((item, index) => {
          const suggestion = item as { name?: string; dosage?: string; reason?: string };
          return (
            <div className="herb-suggestion-card" key={index}>
              <div className="herb-suggestion-header">
                <span className="herb-name">{suggestion.name || `草药 ${index + 1}`}</span>
                {suggestion.dosage && <span className="herb-dosage">{suggestion.dosage}</span>}
              </div>
              {suggestion.reason && <p className="herb-reason">{suggestion.reason}</p>}
            </div>
          );
        })}
      </div>
    );
  }

  if (key === 'patient_info' && value && typeof value === 'object') {
    const patientInfo = value as Record<string, unknown>;
    const profile = (patientInfo as any)?.profile;
    const latestRecord = (patientInfo as any)?.latest_medical_record;
    const recentPrescriptions = (patientInfo as any)?.recent_prescriptions;

    return (
      <div className="patient-info-card">
        {profile && (
          <div className="patient-info-section">
            <h5>基本信息</h5>
            <ul>
              {profile.name && <li>姓名：{profile.name}</li>}
              {profile.age !== undefined && profile.age !== null && <li>年龄：{profile.age}</li>}
              {profile.gender && <li>性别：{profile.gender}</li>}
              {profile.username && <li>账号：{profile.username}</li>}
              {profile.health_conditions && <li>健康状况：{profile.health_conditions}</li>}
            </ul>
          </div>
        )}
        {latestRecord && (
          <div className="patient-info-section">
            <h5>最新病历概览</h5>
            <ul>
              <li>摘要：{latestRecord.summary || '未填写'}</li>
              <li>辨证：{latestRecord.diagnosis || '未填写'}</li>
              {latestRecord.created_at && <li>创建：{formatDateTime(latestRecord.created_at)}</li>}
              {latestRecord.updated_at && <li>更新：{formatDateTime(latestRecord.updated_at)}</li>}
            </ul>
          </div>
        )}
        {Array.isArray(recentPrescriptions) && recentPrescriptions.length > 0 && (
          <div className="patient-info-section">
            <h5>近期处方</h5>
            <ul>
              {recentPrescriptions.map((prescription: any) => (
                <li key={prescription.id}>
                  <strong>{prescription.title || prescription.prescription_id}</strong>
                  {prescription.created_at && (
                    <span>（{formatDateTime(prescription.created_at)}）</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return null;
};

const MedicalRecordDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<MedicalRecordDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      setError('未找到对应的病历编号。');
      setLoading(false);
      return;
    }

    const numericId = Number(id);
    if (Number.isNaN(numericId)) {
      setError('病历编号格式不正确。');
      setLoading(false);
      return;
    }

    let active = true;

    const loadRecord = async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await fetchMedicalRecordDetail(numericId);
        if (!active) return;
        setRecord(detail);
      } catch (err) {
        if (!active) return;
        console.error('加载病历详情失败', err);
        setError('加载病历详情失败，请稍后再试。');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRecord();
    return () => {
      active = false;
    };
  }, [id]);

  const prescriptions = useMemo(() => record?.prescriptions ?? [], [record]);

  const metadataEntries = useMemo(() => {
    if (!record?.metadata || typeof record.metadata !== 'object') {
      return [];
    }
    return Object.entries(record.metadata).filter(
      ([key]) => key !== 'conversation_id' && key !== 'stage',
    );
  }, [record]);

  const metadataMap = useMemo(() => {
    if (!record?.metadata || typeof record.metadata !== 'object') {
      return {} as Record<string, unknown>;
    }
    return record.metadata as Record<string, unknown>;
  }, [record]);

  const herbSupplement = metadataMap['herb_suggestions'];
  const patientInfoSupplement = metadataMap['patient_info'];
  const assistantSummary = typeof metadataMap['assistant_message'] === 'string'
    ? (metadataMap['assistant_message'] as string)
    : null;
  const carePlanSupplement = typeof metadataMap['care_plan_markdown'] === 'string'
    ? (metadataMap['care_plan_markdown'] as string)
    : null;

  const generalMetadataEntries = useMemo(
    () => metadataEntries.filter(([key]) => !SUPPLEMENT_RESERVED_KEYS.includes(key)),
    [metadataEntries],
  );

  const effectiveNotes = useMemo(() => {
    const rawNotes = typeof record?.notes === 'string' ? record.notes.trim() : '';
    if (rawNotes) {
      return rawNotes;
    }
    if (carePlanSupplement && carePlanSupplement.trim().length > 0) {
      return carePlanSupplement.trim();
    }
    return '';
  }, [carePlanSupplement, record?.notes]);

  const hasHerbSupplement = Array.isArray(herbSupplement) && herbSupplement.length > 0;
  const hasPatientInfoSupplement = Boolean(
    patientInfoSupplement && typeof patientInfoSupplement === 'object',
  );
  const hasAssistantSummary = Boolean(assistantSummary && assistantSummary.trim().length > 0);
  const hasSupplementContent =
    hasHerbSupplement || hasPatientInfoSupplement || hasAssistantSummary || generalMetadataEntries.length > 0;
  const herbSuggestionsValue: unknown = hasHerbSupplement ? herbSupplement : undefined;
  const patientInfoValue: unknown = hasPatientInfoSupplement ? patientInfoSupplement : undefined;
  const assistantSummaryValue: string | undefined = hasAssistantSummary ? assistantSummary ?? undefined : undefined;

  const conversationId = metadataMap['conversation_id'] as string | number | undefined;
  const diagnosisStage = metadataMap['stage'] as string | undefined;

  const handleDeleteRecord = async () => {
    if (!record) {
      return;
    }

    const confirmed =
      typeof window === 'undefined'
        ? true
        : window.confirm('删除后将无法恢复该病历，并会同步移除所有关联处方，确认删除？');
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    try {
      await deleteMedicalRecord(record.id);
  navigate('/app/user-profile', { replace: true, state: { focus: 'medical-records' } });
    } catch (deleteErr) {
      console.error('删除病历失败', deleteErr);
      setError('删除病历失败，请稍后再试。');
      setDeleting(false);
    }
  };

  return (
    <motion.div
      className="unified-page-wrapper record-detail-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
    >
      <div className="unified-page-header record-detail-header">
        <h2>病历详情</h2>
        <div className="record-header-actions">
          <button
            type="button"
            className="record-delete-button"
            onClick={handleDeleteRecord}
            disabled={deleting || loading}
          >
            {deleting ? '删除中...' : '删除病历'}
          </button>
          <button
            type="button"
            className="record-back-button"
            onClick={() => navigate(-1)}
          >
            返回上一页
          </button>
        </div>
      </div>

      <div className="unified-page-content-scrollable record-detail-content">
        {loading ? (
          <div className="detail-loading">正在加载病历详情...</div>
        ) : error ? (
          <div className="detail-error">{error}</div>
        ) : !record ? (
          <div className="detail-empty">未查询到对应病历信息。</div>
        ) : (
          <div className="record-detail-layout">
            <section className="detail-card highlight">
              <div className="detail-card-header">
                <span className="detail-badge">就诊编号 #{record.id}</span>
                <span className="detail-time">最近更新：{formatDateTime(record.updated_at || record.created_at)}</span>
              </div>
              <h3>{record.summary || '未填写病历摘要'}</h3>
              <p className="detail-diagnosis">辨证结论：{record.diagnosis || '未填写'}</p>
              {(conversationId || diagnosisStage) && (
                <div className="detail-chip-group">
                  {conversationId && (
                    <span className="detail-chip">会话编号 #{String(conversationId)}</span>
                  )}
                  {diagnosisStage && (
                    <span className="detail-chip soft">阶段：{String(diagnosisStage)}</span>
                  )}
                </div>
              )}
              <div className="detail-meta-grid">
                <div>
                  <span className="meta-label">首诊时间</span>
                  <span className="meta-value">{formatDateTime(record.created_at)}</span>
                </div>
                <div>
                  <span className="meta-label">最后更新</span>
                  <span className="meta-value">{formatDateTime(record.updated_at)}</span>
                </div>
                <div>
                  <span className="meta-label">关联处方</span>
                  <span className="meta-value">{prescriptions.length} 条</span>
                </div>
              </div>
            </section>

            <section className="detail-card">
              <h4>主诉与症状</h4>
              <p className="detail-paragraph">{record.symptoms || '未填写'}</p>
              <h4>医嘱与调理建议</h4>
              {effectiveNotes ? (
                renderMarkdownContent(effectiveNotes)
              ) : (
                <p className="detail-paragraph subtle">未填写</p>
              )}
            </section>

            <section className="detail-card">
              <h4>关联处方</h4>
              {prescriptions.length === 0 ? (
                <div className="detail-empty subtle">暂无关联处方</div>
              ) : (
                <ul className="detail-prescription-list">
                  {prescriptions.map((item) => (
                    <li key={item.id}>
                      <div className="prescription-info">
                        <strong>{item.title || `处方 ${item.prescription_id}`}</strong>
                        <span>{formatDateTime(item.created_at)}</span>
                      </div>
                      <Link className="detail-link" to={`/app/prescriptions/${item.prescription_id}`}>
                        查看处方详情
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="detail-card">
              <h4>补充资料</h4>
              {!hasSupplementContent ? (
                <div className="detail-empty subtle">暂无补充说明</div>
              ) : (
                <div className="supplement-grid">
                  {hasHerbSupplement && (
                    <div className="supplement-card herb-card">
                      <div className="supplement-title">{getMetadataLabel('herb_suggestions')}</div>
                      {renderSpecialMetadata('herb_suggestions', herbSuggestionsValue ?? [])}
                    </div>
                  )}

                  {hasPatientInfoSupplement && (
                    <div className="supplement-card">
                      <div className="supplement-title">{getMetadataLabel('patient_info')}</div>
                      {renderSpecialMetadata('patient_info', patientInfoValue ?? {})}
                    </div>
                  )}

                  {hasAssistantSummary && assistantSummaryValue && (
                    <div className="supplement-card">
                      <div className="supplement-title">{getMetadataLabel('assistant_message')}</div>
                      {renderSpecialMetadata('assistant_message', assistantSummaryValue)}
                    </div>
                  )}

                  {generalMetadataEntries.map(([key, value]) => (
                    <div key={key} className="supplement-card compact">
                      <div className="supplement-title">{getMetadataLabel(key)}</div>
                      <div className="supplement-body">
                        {renderSpecialMetadata(key, value) ?? renderMetadataValue(value)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MedicalRecordDetailPage;
